import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { apiRoute } from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { PRODUCT_PREVIEW_IMAGE_SIZE } from '../../lib/images';
import { setData } from '../../lib/storage';
import Carousel from '../common/carousel';
import Grid from '../common/grid';
import List from '../common/list';
import Loading from '../common/loading';
import ProductImage from '../product/image';
import { useUser } from '../user-context';
import Link from 'next/link';

const DynamicAddToCart = dynamic(() => import('../cart/add-to-cart'));

const WithPromoCode = ({ primary, fields }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { checkout } = useUser();
  const { query } = router;
  const [validationCheck, setValidationCheck] = useState(undefined);

  const filter =
    primary.voucher_filter?.length > 0
      ? primary.voucher_filter[0].text.toLowerCase()
      : '';

  const item = fields[0];
  const line = checkout?.lines.find(
    (l) => l.variant.id === item.defaultVariant.id,
  );

  useEffect(() => {
    if (query.voucher) {
      if (query.voucher.toLowerCase().indexOf(filter) === 0) {
        apiRoute('/validate_voucher?voucher_code=' + query.voucher).then(
          (res) => {
            item.isAvailableForPurchase =
              (!line?.quantity || line.quantity === 0) && res.valid;

            if (res.valid) {
              setData(item.defaultVariant.id, query.voucher);
            }
            setValidationCheck(res);
          },
        );
      } else {
        item.isAvailableForPurchase = false;
        setValidationCheck({ valid: false });
      }
    } else {
      item.isAvailableForPurchase = !line?.quantity || line.quantity === 0;
      setValidationCheck({ valid: true });
    }
  }, [query.voucher, line?.quantity]);

  return (
    <div className='flex items-center flex-col gap-4'>
      <ProductImage
        size={PRODUCT_PREVIEW_IMAGE_SIZE}
        className={'w-full mb-3'}
        product={item}
        showDiscount={false}
      />
      {validationCheck !== undefined ? (
        <>
          <DynamicAddToCart
            product={item}
            className=''
            button
            subPriceText={primary.button_label}
            initialQuantity={1}
          />

          {query.voucher && (
            <div
              className={`flex items-center gap-1  ${validationCheck.valid ? 'text-green' : 'line-through'}`}
            >
              <span className='text-black'>{t('code')}</span>
              <span className='whitespace-nowrap	'>{query.voucher}</span>

              {validationCheck.valid && (
                <svg
                  id='Layer_1'
                  xmlns='http://www.w3.org/2000/svg'
                  version='1.1'
                  viewBox='0 0 110 135'
                  className='w-6 fill-current mt-[2px]'
                >
                  <path
                    className='st0'
                    d='M55,15c24.9,0,45,20.1,45,45s-20.1,45-45,45S10,84.9,10,60,30.1,15,55,15ZM70.5,44.2l-21.8,25.1-9.3-10.7c-2.6-3-7.1.9-4.5,3.9l11.5,13.3c1.2,1.4,3.3,1.4,4.5,0l24-27.7c2.6-3-1.9-6.9-4.5-3.9h0ZM82.6,32.4c-15.2-15.2-39.9-15.2-55.2,0-15.2,15.2-15.2,39.9,0,55.2s39.9,15.2,55.2,0c15.2-15.2,15.2-39.9,0-55.2Z'
                  />
                </svg>
              )}
            </div>
          )}
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
};


export const ProductCard = ({
  product, 
  eyebrow=  'TALES & TERROIR',
  reverse = false,
  enableAddToCart = false,
}) => {
const { attrT, t } = useTranslation();
const lead = attrT(product.metadata, 'lead');
const cardTitle = attrT(product.metadata, 'cardTitle');
const cardDescription = attrT(product.metadata, 'cardDescription');
const cardHoverUrl = product.metadata?.['cardHoverUrl'];

const [isHovered, setIsHovered] = useState(false);
const defaultImageUrl = product.images[0]?.url;

return (
  <div
    data-product-card
    className={`flex flex-col gap-10 rounded-[24px] bg-white py-4 md:p-6 lg:p-10 md:flex-row md:items-center ${reverse ? 'md:flex-row-reverse' : ''}`}
  >
    <div className='mx-auto w-full max-w-sm flex-shrink-0 md:max-w-none md:basis-[44%]'>
      <div 
        className='relative flex items-center justify-center overflow-hidden bg-white'
        onMouseEnter={() => cardHoverUrl && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link
            href={{
                pathname: '/[slug]/[product]',
                query: {
                    slug: product.category.slug,
                    product: product.slug,
                },
            }}
            className='relative block w-full'
          >
            {/* Default image - always rendered to maintain container size */}
            <img 
              src={defaultImageUrl} 
              alt={cardTitle} 
              className={`h-full w-full object-contain transition-opacity duration-300 ${isHovered && cardHoverUrl ? 'opacity-0' : 'opacity-100'}`}
            />
            {/* Hover image - positioned absolutely on top */}
            {cardHoverUrl && (
              <img 
                src={cardHoverUrl} 
                alt={cardTitle} 
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
              />
            )}
          </Link>
      </div>
    </div>

    <div className='w-full md:basis-[56%] md:px-8'>
      {eyebrow && (
        <div className='mb-1 text-xs font-semibold uppercase text-black'>
          {eyebrow}
        </div>
      )}
      <h3 className='mb-3 text-[24px] md:text-[32px] lg:text-[48px] leading-[1.1] font-[300] uppercase text-black'>
        {cardTitle}
      </h3>
      <div className='m2-6 text-[17px] uppercase text-black font-[700]'>
        {lead}
      </div>
      <p className='mb-6 max-w-xl text-[17px] text-black '>{cardDescription}</p>
      <div className='flex flex-row items-center gap-2 justify-center md:justify-start'>
        <Link
          href={{
              pathname: '/[slug]/[product]',
              query: {
                  slug: product.category.slug,
                  product: product.slug,
              },
          }}
          className={`rounded-md inline-flex items-center justify-center border border-black ${enableAddToCart ? 'bg-white hover:bg-black hover:text-white text-black' : 'bg-black hover:bg-white hover:text-black text-white'} px-5 pt-3 pb-[0.65rem] text-xs font-semibold uppercase transition-colors duration-150`}
        >
          {t('mehr_erfahren')}
        </Link>

        {/* {enableAddToCart && (
        <DynamicAddToCart
          product={product}
          allowMultipleAdd={false}
          button={true}
          priceMeta={undefined}
          overwriteInner={Button}
          initialQuantity={1}
          theme='dark'
        />
        )} */}
      </div>
    </div>
  </div>
);
};



export const ProductsSlice = ({ primary, fields, className = '' }) => {
  if (primary.style === 'with_promo_code') {
    return <WithPromoCode primary={primary} fields={fields} />;
  }

  if (primary.style === 'cards') {
    return <div className='flex flex-col gap-4 md:gap-8 lg:gap-10'>
    {fields.map((product, index) => (
      <ProductCard
        key={index}
        reverse={index % 2 === 1}
        product={product}
      />
    ))}
    </div>;
  }

  return (
    <List
      items={fields}
      Wrapper={primary.style === 'grid' ? Grid : Carousel}
      className={className}
      listName='cms products slice'
    />
  );
};
